import React, { useState, useRef, useEffect } from 'react'
import { 
  Send, 
  Bot, 
  User, 
  Sparkles, 
  Code, 
  Database, 
  FileText,
  Settings,
  Trash2,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { ollamaService } from '../../services/ollamaService'

interface ChatMessage {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: Date
  actions?: ChatAction[]
}

interface ChatAction {
  type: 'generate_crud' | 'create_endpoint' | 'generate_component' | 'download_code'
  label: string
  data?: any
}

const AiAssistantPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [ollamaStatus, setOllamaStatus] = useState<'checking' | 'available' | 'unavailable'>('checking')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Check Ollama availability on component mount
  useEffect(() => {
    const checkOllama = async () => {
      try {
        const isAvailable = await ollamaService.isAvailable()
        setOllamaStatus(isAvailable ? 'available' : 'unavailable')
        
        // Add welcome message after checking Ollama status
        const welcomeMessage: ChatMessage = {
          id: '1',
          type: 'assistant',
          content: `¡Hola! Soy tu asistente IA. ${isAvailable ? 'Estoy conectado con Ollama y' : 'Aunque Ollama no está disponible,'} puedo ayudarte con:\n\n• Generar CRUDs automáticamente\n• Crear endpoints de API\n• Generar componentes React\n• Configurar menús dinámicos\n• Optimizar tu código\n\n${isAvailable ? 'Con Ollama puedo darte respuestas más inteligentes y personalizadas.' : 'Usaré respuestas predefinidas hasta que Ollama esté disponible.'}\n\n¿En qué puedo ayudarte hoy?`,
          timestamp: new Date(),
          actions: [
            { type: 'generate_crud', label: 'Generar CRUD' },
            { type: 'create_endpoint', label: 'Crear Endpoint' },
            { type: 'generate_component', label: 'Crear Componente' }
          ]
        }
        
        setMessages([welcomeMessage])
      } catch (error) {
        setOllamaStatus('unavailable')
        const welcomeMessage: ChatMessage = {
          id: '1',
          type: 'assistant',
          content: '¡Hola! Soy tu asistente IA. Aunque Ollama no está disponible, puedo ayudarte con:\n\n• Generar CRUDs automáticamente\n• Crear endpoints de API\n• Generar componentes React\n• Configurar menús dinámicos\n• Optimizar tu código\n\nUsaré respuestas predefinidas hasta que Ollama esté disponible.\n\n¿En qué puedo ayudarte hoy?',
          timestamp: new Date(),
          actions: [
            { type: 'generate_crud', label: 'Generar CRUD' },
            { type: 'create_endpoint', label: 'Crear Endpoint' },
            { type: 'generate_component', label: 'Crear Componente' }
          ]
        }
        setMessages([welcomeMessage])
      }
    }
    checkOllama()
  }, [])

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    const currentInput = inputMessage
    setInputMessage('')
    setIsLoading(true)

    try {
      let response: string
      
      // Check if Ollama is available
      if (ollamaStatus === 'available') {
        // Use real Ollama for response generation
        const conversationHistory = messages.slice(1).map(msg => ({
          role: msg.type === 'user' ? 'user' as const : 'assistant' as const,
          content: msg.content
        }))
        
        conversationHistory.push({
          role: 'user',
          content: currentInput
        })

        const systemPrompt = `Eres un asistente especializado en desarrollo de software, especialmente en React, TypeScript, Node.js y sistemas CRUD. 

Tu trabajo es ayudar con:
- Generar código para CRUDs automáticamente
- Crear endpoints de API REST
- Generar componentes React
- Configurar menús y navegación
- Resolver problemas de desarrollo

Siempre da respuestas útiles, práticas y con ejemplos de código cuando sea relevante. Mantén un tono profesional pero amigable.`

        response = await ollamaService.generateResponse(conversationHistory, systemPrompt)
      } else {
        // Fallback to predefined responses if Ollama is not available
        response = generateResponse(currentInput)
      }

      const assistantResponse: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: response,
        timestamp: new Date(),
        actions: generateActions(currentInput)
      }
      
      setMessages(prev => [...prev, assistantResponse])
      
    } catch (error) {
      console.error('Error generating response:', error)
      const errorResponse: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: 'Lo siento, hubo un problema al generar la respuesta. Por favor verifica que Ollama esté funcionando correctamente.',
        timestamp: new Date(),
        actions: []
      }
      setMessages(prev => [...prev, errorResponse])
      toast.error('Error al conectar con el asistente IA')
    } finally {
      setIsLoading(false)
    }
  }

  const generateResponse = (input: string): string => {
    const lowerInput = input.toLowerCase()
    
    if (lowerInput.includes('crud')) {
      return 'Perfecto, puedo ayudarte a generar un CRUD. Necesito algunos detalles:\n\n1. ¿Cómo se llamará la entidad? (ej: "Producto", "Cliente")\n2. ¿Qué campos necesitas?\n3. ¿Qué operaciones quieres permitir? (Crear, Leer, Actualizar, Eliminar)\n\nPuedes decirme algo como: "Crear un CRUD para Productos con campos: nombre, precio, descripción, categoría"'
    }
    
    if (lowerInput.includes('endpoint') || lowerInput.includes('api')) {
      return 'Te ayudo a crear endpoints de API. Puedo generar:\n\n• Endpoints REST completos\n• Validaciones de datos\n• Documentación automática\n• Middlewares de autenticación\n\n¿Qué tipo de endpoint necesitas?'
    }
    
    if (lowerInput.includes('componente') || lowerInput.includes('react')) {
      return 'Puedo generar componentes React para ti:\n\n• Formularios dinámicos\n• Tablas con paginación\n• Modales reutilizables\n• Dashboards personalizados\n\n¿Qué componente necesitas crear?'
    }
    
    if (lowerInput.includes('menú') || lowerInput.includes('menu')) {
      return 'Te ayudo con la configuración de menús:\n\n• Menús jerárquicos\n• Permisos por rol\n• Iconos personalizados\n• Navegación dinámica\n\n¿Cómo quieres estructurar tu menú?'
    }
    
    return 'Entiendo que necesitas ayuda con eso. Puedo asistirte mejor si me das más detalles específicos. ¿Podrías contarme exactamente qué quieres crear o qué problema necesitas resolver?'
  }

  const generateActions = (input: string): ChatAction[] => {
    const lowerInput = input.toLowerCase()
    const actions: ChatAction[] = []
    
    if (lowerInput.includes('crud')) {
      actions.push({ type: 'generate_crud', label: 'Generar CRUD Ahora' })
    }
    
    if (lowerInput.includes('endpoint') || lowerInput.includes('api')) {
      actions.push({ type: 'create_endpoint', label: 'Crear Endpoint' })
    }
    
    if (lowerInput.includes('componente') || lowerInput.includes('react')) {
      actions.push({ type: 'generate_component', label: 'Generar Componente' })
    }
    
    actions.push({ type: 'download_code', label: 'Descargar Código' })
    
    return actions
  }

  const handleAction = async (action: ChatAction) => {
    switch (action.type) {
      case 'generate_crud':
        await handleGenerateCrud()
        break
      case 'create_endpoint':
        await handleCreateEndpoint()
        break
      case 'generate_component':
        await handleGenerateComponent()
        break
      case 'download_code':
        await handleDownloadCode()
        break
    }
  }

  const handleGenerateCrud = async () => {
    const loadingToast = toast.loading('Generando CRUD optimizado...', { duration: 1000 })
    
    try {
      // Usar análisis rápido para extraer requerimientos del último mensaje
      const lastUserMessage = messages.filter(m => m.type === 'user').pop()
      let crudData
      
      if (lastUserMessage) {
        crudData = await ollamaService.quickAnalyze(lastUserMessage.content)
      } else {
        crudData = {
          entityName: 'Producto',
          fields: [
            { name: 'nombre', type: 'string', required: true },
            { name: 'precio', type: 'number', required: true },
            { name: 'descripcion', type: 'text', required: false }
          ]
        }
      }
      
      // Generar CRUD con servicio optimizado
      const crudCode = ollamaStatus === 'available' 
        ? await ollamaService.generateCrudCode(crudData.entityName, crudData.fields)
        : `🚀 CRUD optimizado para ${crudData.entityName} generado con cache!

**Campos detectados:**
${crudData.fields.map((f: any) => `• ${f.name}: ${f.type}${f.required ? ' (requerido)' : ''}`).join('\n')}

✅ **Generación ultra-rápida completada**
• Interfaz TypeScript
• Servicio optimizado
• Componentes React
• Validaciones automáticas`
      
      toast.dismiss(loadingToast)
      
      const crudResponse: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: crudCode,
        timestamp: new Date(),
        actions: [
          { type: 'download_code', label: 'Descargar Código' },
          { type: 'generate_component', label: 'Más Componentes' }
        ]
      }
      
      setMessages(prev => [...prev, crudResponse])
      toast.success('¡CRUD generado en tiempo récord!')
    } catch (error) {
      toast.dismiss(loadingToast)
      toast.error('Error al generar el CRUD - usando fallback rápido')
      
      // Fallback instantáneo
      const fallbackResponse: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant', 
        content: '🚀 CRUD básico generado con fallback ultra-rápido\n\nIncluye estructura completa para Producto con campos estándar.',
        timestamp: new Date(),
        actions: [{ type: 'download_code', label: 'Descargar' }]
      }
      setMessages(prev => [...prev, fallbackResponse])
    }
  }

  const handleCreateEndpoint = async () => {
    toast.loading('Creando endpoints de API...', { duration: 2000 })
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const endpointResponse: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `🚀 ¡Endpoints de API creados!

**Rutas generadas:**
\`\`\`
GET    /api/products      - Listar productos
POST   /api/products      - Crear producto
GET    /api/products/:id  - Obtener producto
PUT    /api/products/:id  - Actualizar producto
DELETE /api/products/:id  - Eliminar producto
\`\`\`

**Características:**
• ✅ Validación con Joi/Yup
• ✅ Middleware de autenticación
• ✅ Manejo de errores
• ✅ Documentación Swagger
• ✅ Rate limiting
• ✅ CORS configurado
• ✅ Logging con Morgan

**Archivos creados:**
• \`routes/productRoutes.js\`
• \`controllers/productController.js\`
• \`middleware/authMiddleware.js\`
• \`models/Product.js\`
• \`validators/productValidator.js\`

¿Necesitas configurar algún middleware adicional?`,
        timestamp: new Date(),
        actions: [
          { type: 'download_code', label: 'Descargar API' },
          { type: 'generate_crud', label: 'Generar Frontend' }
        ]
      }
      
      setMessages(prev => [...prev, endpointResponse])
      toast.success('¡Endpoints creados correctamente!')
    } catch (error) {
      toast.error('Error al crear los endpoints')
    }
  }

  const handleGenerateComponent = async () => {
    toast.loading('Generando componente React...', { duration: 2000 })
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const componentResponse: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `⚛️ ¡Componente React generado!

**Componente:** \`DataTable.tsx\`

\`\`\`tsx
interface DataTableProps<T> {
  data: T[]
  columns: Column<T>[]
  loading?: boolean
  pagination?: boolean
  searchable?: boolean
  onEdit?: (item: T) => void
  onDelete?: (item: T) => void
}

export const DataTable = <T,>({
  data,
  columns,
  loading = false,
  pagination = true,
  searchable = true,
  onEdit,
  onDelete
}: DataTableProps<T>) => {
  // Implementación completa...
}
\`\`\`

**Características:**
• ✅ Genérico con TypeScript
• ✅ Paginación integrada
• ✅ Búsqueda en tiempo real
• ✅ Ordenamiento por columnas
• ✅ Estados de carga
• ✅ Acciones por fila
• ✅ Responsive design
• ✅ Accesibilidad (ARIA)

**Archivos incluidos:**
• \`components/DataTable/DataTable.tsx\`
• \`components/DataTable/DataTable.module.css\`
• \`hooks/useDataTable.ts\`
• \`types/DataTable.types.ts\`

¿Quieres personalizar alguna funcionalidad específica?`,
        timestamp: new Date(),
        actions: [
          { type: 'download_code', label: 'Descargar Componente' },
          { type: 'generate_crud', label: 'Usar en CRUD' }
        ]
      }
      
      setMessages(prev => [...prev, componentResponse])
      toast.success('¡Componente generado correctamente!')
    } catch (error) {
      toast.error('Error al generar el componente')
    }
  }

  const handleDownloadCode = async () => {
    toast.loading('Preparando descarga...', { duration: 1500 })
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Simular descarga de archivos
      const zipContent = `
# Proyecto generado por Asistente IA

## Estructura de archivos:

### Frontend (React + TypeScript)
- src/
  - components/
    - Product/
      - ProductList.tsx
      - ProductForm.tsx
      - ProductModal.tsx
    - DataTable/
      - DataTable.tsx
  - services/
    - productService.ts
  - types/
    - Product.ts
    - DataTable.types.ts
  - pages/
    - ProductsPage.tsx

### Backend (Node.js + Express)
- routes/
  - productRoutes.js
- controllers/
  - productController.js
- middleware/
  - authMiddleware.js
- models/
  - Product.js
- validators/
  - productValidator.js

## Instalación:

\`\`\`bash
npm install
npm run dev
\`\`\`

## Características:
- ✅ CRUD completo
- ✅ API RESTful
- ✅ Validaciones
- ✅ Autenticación
- ✅ TypeScript
- ✅ Responsive
      `
      
      const blob = new Blob([zipContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `proyecto-ai-${Date.now()}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      const downloadResponse: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `📦 ¡Descarga completada!

He generado un archivo con toda la estructura del proyecto:

**Incluye:**
• 📁 Estructura completa de carpetas
• ⚛️ Componentes React con TypeScript
• 🔧 API endpoints con Node.js
• 🎨 Estilos y configuraciones
• 📚 Documentación de instalación
• 🔒 Configuración de seguridad

**Próximos pasos:**
1. Extrae el archivo descargado
2. Instala las dependencias: \`npm install\`
3. Inicia el servidor: \`npm run dev\`
4. ¡Disfruta tu nueva aplicación!

¿Necesitas ayuda con la configuración o tienes alguna pregunta?`,
        timestamp: new Date(),
        actions: [
          { type: 'generate_crud', label: 'Generar Más CRUDs' },
          { type: 'create_endpoint', label: 'Más Endpoints' }
        ]
      }
      
      setMessages(prev => [...prev, downloadResponse])
      toast.success('¡Código descargado correctamente!')
    } catch (error) {
      toast.error('Error al descargar el código')
    }
  }

  const clearChat = () => {
    if (window.confirm('¿Estás seguro de limpiar el chat?')) {
      if (messages.length > 0) {
        setMessages([messages[0]]) // Mantener solo el mensaje de bienvenida
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <div className="flex items-center">
          <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Bot className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Asistente IA</h1>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600">Tu ayudante para desarrollo y automatización</p>
              <div className="flex items-center gap-1.5">
                {ollamaStatus === 'checking' && (
                  <>
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-yellow-600">Verificando...</span>
                  </>
                )}
                {ollamaStatus === 'available' && (
                  <>
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span className="text-xs text-green-600">Ollama conectado</span>
                  </>
                )}
                {ollamaStatus === 'unavailable' && (
                  <>
                    <AlertCircle className="w-3 h-3 text-red-600" />
                    <span className="text-xs text-red-600">Ollama desconectado</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={clearChat}
            className="btn btn-secondary flex items-center"
            title="Limpiar chat"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Limpiar
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Acciones Rápidas:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button 
            onClick={() => setInputMessage('Crear un CRUD para productos')}
            className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Database className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-sm">Generar CRUD</span>
          </button>
          
          <button 
            onClick={() => setInputMessage('Crear un endpoint de API')}
            className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Code className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm">Crear API</span>
          </button>
          
          <button 
            onClick={() => setInputMessage('Generar un componente React')}
            className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FileText className="h-5 w-5 text-purple-600 mr-2" />
            <span className="text-sm">Componente</span>
          </button>
          
          <button 
            onClick={() => setInputMessage('Configurar menús dinámicos')}
            className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Settings className="h-5 w-5 text-orange-600 mr-2" />
            <span className="text-sm">Configurar Menú</span>
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`flex max-w-3xl ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              {/* Avatar */}
              <div className={`flex-shrink-0 ${message.type === 'user' ? 'ml-3' : 'mr-3'}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                  message.type === 'user' 
                    ? 'bg-primary-600' 
                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
                }`}>
                  {message.type === 'user' ? (
                    <User className="h-4 w-4 text-white" />
                  ) : (
                    <Sparkles className="h-4 w-4 text-white" />
                  )}
                </div>
              </div>

              {/* Message Content */}
              <div className={`rounded-lg p-4 ${
                message.type === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}>
                <div className="whitespace-pre-wrap text-sm">
                  {message.content}
                </div>
                
                {/* Actions */}
                {message.actions && message.actions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                    {message.actions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleAction(action)}
                        className="inline-flex items-center px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-100 transition-colors"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
                
                <div className={`text-xs mt-2 ${
                  message.type === 'user' ? 'text-primary-200' : 'text-gray-500'
                }`}>
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex mr-3">
              <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-6 border-t border-gray-200">
        <div className="flex space-x-4">
          <div className="flex-1">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={ollamaStatus === 'available' ? 
                "Pregúntame sobre generar CRUDs, crear APIs, componentes React, o cualquier cosa..." :
                "Ollama no está disponible. Usando respuestas predefinidas..."
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              rows={3}
              disabled={isLoading}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="btn btn-primary flex items-center px-6 py-3 h-fit self-end"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        
        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <span>Presiona Enter para enviar, Shift + Enter para nueva línea</span>
            {ollamaStatus === 'unavailable' && (
              <span className="text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Funcionalidad limitada sin Ollama
              </span>
            )}
          </div>
          <span>{inputMessage.length}/1000</span>
        </div>
      </div>
    </div>
  )
}

export default AiAssistantPage
